import { ChevronRight } from 'lucide-react'
import DeltaBadge from './DeltaBadge'
import { EN_DASH } from './leagueDetailFormat'

interface LeagueStickyBarProps {
  leagueName: string
  yourRank: number
  yourPoints: number
  rankDelta: number | null
  gapToAbove: number | null
  // Season not open yet: rank and points read as en dashes and no delta/gap renders.
  isSeasonOpener: boolean
  isCollapsed: boolean
  isUserRowVisible: boolean
  onJump: () => void
}

/**
 * Sticky collapsed header — the single sticky element on this screen. Docked
 * full-bleed with an opaque page-background fill so rows can't ghost through, it swaps in on
 * collapse via a 120ms crossfade and, on tap, jumps to the user's row. Only rendered when there's a
 * "you" row to summarise/jump to.
 */
function LeagueStickyBar({
  leagueName,
  yourRank,
  yourPoints,
  rankDelta,
  gapToAbove,
  isSeasonOpener,
  isCollapsed,
  isUserRowVisible,
  onJump,
}: LeagueStickyBarProps) {
  return (
    <button
      type="button"
      className={`league-detail__sticky ${
        isCollapsed ? 'league-detail__sticky--visible' : ''
      }`}
      onClick={onJump}
      aria-hidden={!isCollapsed}
      tabIndex={isCollapsed ? 0 : -1}
      aria-label={`Jump to your row in ${leagueName}`}
    >
      <span className="league-detail__sticky-inner">
        <span className="league-detail__sticky-name">{leagueName}</span>
        <span
          className={`league-detail__sticky-user ${
            isUserRowVisible ? 'league-detail__sticky-user--hidden' : ''
          }`}
          aria-hidden={isUserRowVisible}
        >
          {!isSeasonOpener && (
            <DeltaBadge
              delta={rankDelta}
              baseClass="league-detail__sticky-delta"
            />
          )}
          <span className="league-detail__sticky-rank">
            {isSeasonOpener ? EN_DASH : `#${yourRank.toLocaleString()}`}
          </span>
          {!isSeasonOpener && gapToAbove != null && gapToAbove > 0 && (
            <span className="league-detail__sticky-gap">{`+${gapToAbove}`}</span>
          )}
          <span className="league-detail__sticky-points">
            {isSeasonOpener ? EN_DASH : yourPoints.toLocaleString()}
          </span>
          <ChevronRight
            size={16}
            className="league-detail__sticky-chevron"
            aria-hidden="true"
          />
        </span>
      </span>
    </button>
  )
}

export default LeagueStickyBar
